import { useMutation } from "@apollo/client/react";
import {
  AdminArticleFragment,
  ContentStatus,
  UpdateArticle,
  UpdateArticleVariables,
} from "__generated__/types";
import { CancelOrSubmit } from "common/buttons";
import { MarkdownField } from "common/components/forms/MarkdownField";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { TextField } from "common/components/forms/TextField";
import { Select } from "common/components/Select";
import { contentStatusOptions } from "common/content-status";
import {
  extractNullableMarkdownContent,
  markdownInput,
} from "common/input-types";
import { isUrl, youtubeUrlToId } from "common/youtube";
import gql from "graphql-tag";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";

const MUTATION = gql`
  mutation UpdateArticle($input: UpdateArticleDetailsInput!) {
    updateArticleDetails(input: $input) {
      article {
        id
        title
        body {
          content
          isMarkdown
        }
        relevantContent {
          content
          isMarkdown
        }
        youtubeVideoId
      }
    }
  }
`;

const validateYouTubeInput = (input: string): string | undefined => {
  const isValid =
    input === ""
      ? true
      : isUrl(input)
        ? youtubeUrlToId(input) !== null
        : input.length === 11;

  return isValid ? undefined : "Verdien er ikke en gyldig URL eller ID.";
};

interface FormValues {
  id: string;
  status: ContentStatus;
  title: string;
  body: string;
  relevantContent: string;
  youtubeVideoId: string;
}

interface Props {
  cancel(): void;
  complete(): void;
  article: AdminArticleFragment;
}

export const ArticleEditForm: React.FC<React.PropsWithChildren<Props>> = ({
  cancel,
  complete,
  article,
}) => {
  const methods = useForm<FormValues>({
    defaultValues: {
      body: extractNullableMarkdownContent(article.body) ?? "",
      relevantContent:
        extractNullableMarkdownContent(article.relevantContent) ?? "",
      status: article.status,
    },
  });

  const [mutate, { error, loading }] = useMutation<
    UpdateArticle,
    UpdateArticleVariables
  >(MUTATION);

  const onSubmit = async (input: FormValues) => {
    const result = await mutate({
      variables: {
        input: {
          id: article.id,
          status: {
            updatedValue: input.status,
          },
          title: {
            updatedValue: input.title,
          },
          youtubeVideoId: {
            updatedValue:
              input.youtubeVideoId == null
                ? null
                : isUrl(input.youtubeVideoId)
                  ? youtubeUrlToId(input.youtubeVideoId)
                  : input.youtubeVideoId,
          },
          body: {
            updatedValue: input.body == null ? null : markdownInput(input.body),
          },
          relevantContent: {
            updatedValue:
              input.relevantContent == null
                ? null
                : markdownInput(input.relevantContent),
          },
        },
      },
    });

    if (result.data) {
      complete();
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <TextField
          title="Tittel"
          fieldProps={methods.register("title", {
            required: "Artikkelen må ha en tittel",
          })}
          defaultValue={article.title}
        />

        <Select
          options={contentStatusOptions()}
          title="Status"
          fieldProps={methods.register("status")}
        />

        <TextField
          title="YouTube-video"
          placeholder="Skriv inn URL eller ID til YouTube-video.."
          fieldProps={methods.register("youtubeVideoId", {
            validate: validateYouTubeInput,
          })}
          defaultValue={article.youtubeVideoId ?? ""}
        />

        <MarkdownField<FormValues> name="body" title="Tekst" />

        <MarkdownField<FormValues>
          name="relevantContent"
          title="Relevant innhold"
        />

        <MutationLoadingOrError loading={loading} error={error} />

        <CancelOrSubmit
          onCancel={cancel}
          submitText="Lagre"
          isLoading={loading}
        />
      </form>
    </FormProvider>
  );
};
